<script setup lang="ts">
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconLogout,
  IconX,
} from '@tabler/icons-vue';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { getNavItems } from '~/config/routes';
import useSidebar from '~/hooks/useSidebar';
import useAuth from '~/store/auth/useAuth';
import useLogout from '~/store/auth/useLogout';

const { data: auth } = useAuth();
const logout = useLogout();
const route = useRoute();

// Sidebar state management
const {
  collapseSidebar,
  isCollapsed,
  isMobile,
  setMobile,
  toggleSidebar,
} = useSidebar();
const openSubMenus = ref<Set<string>>(new Set());
const hoveredParentItem = ref<null | string>(null);
const hideTimer = ref<null | number>(null);

// Navigation items
const navItems = computed(() => {
  if (!auth.value) return [];
  return getNavItems(auth.value.hasSomeRole, auth.value.hasSomeGroup);
});

// Mobile detection
const checkMobile = () => {
  const mobile = window.innerWidth < 992;
  setMobile(mobile);
};

// Show toggle button (hidden only on mobile)
const showToggleButton = computed(() => !isMobile.value);

// Toggle submenu (only for desktop)
const toggleSubMenu = (label: string) => {
  if (isCollapsed.value || isMobile.value) return; // Don't toggle when collapsed or on mobile

  if (openSubMenus.value.has(label)) {
    openSubMenus.value.delete(label);
  } else {
    openSubMenus.value.add(label);
  }
};

// Close sidebar on mobile when clicking outside
const sidebarRef = ref<HTMLElement | null>(null);

const handleClickOutside = (event: MouseEvent) => {
  if (
    isMobile.value
    && !isCollapsed.value
    && sidebarRef.value
    && !sidebarRef.value.contains(event.target as Node)
  ) {
    collapseSidebar();
  }
};

// Lifecycle
onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile);
  document.removeEventListener('mousedown', handleClickOutside);

  // Clear any pending hide timer
  if (hideTimer.value) {
    clearTimeout(hideTimer.value);
  }
});

// Close sidebar on mobile after navigation
const closeSidebarOnMobile = () => {
  if (isMobile.value) {
    // Close sidebar after a short delay to allow navigation to complete
    setTimeout(() => {
      collapseSidebar();
    }, 50);
  }
};

// Helper to ensure valid router links
const getValidRoute = (routePath: string | undefined) => routePath || '/';

// Check if a route should be marked as active
// Special case: /financing/[id] should mark "Financiamentos" as active
const isRouteActive = (routePath: string | undefined) => {
  if (!routePath) return false;
  const currentPath = route.path;

  // If the route is /financings, check if current path matches
  if (routePath === '/financings') {
    return currentPath === '/financings' || currentPath.startsWith('/financing/');
  }

  // For other routes, use default Vue Router matching
  return currentPath === routePath || currentPath.startsWith(`${routePath}/`);
};

// Check if any child of a parent menu is active
const hasActiveChild = (item: { children?: Array<{ to?: string }> }) => {
  if (!item.children) return false;
  return item.children.some((child) => isRouteActive(child.to));
};

// Auto-open parent menus when a child is active
watch(
  () => route.path,
  () => {
    if (isMobile.value || isCollapsed.value) return;

    navItems.value.forEach((item) => {
      if (item.children && hasActiveChild(item)) {
        openSubMenus.value.add(item.label);
      }
    });
  },
  { immediate: true },
);

// Generate tooltip for collapsed parent items
const getParentTooltip = (item: { children?: Array<{ label: string }>; label: string }) => {
  if (!isCollapsed.value || isMobile.value) return '';
  if (!item.children) return item.label;

  const childLabels = item.children.map((child) => child.label).join(', ');
  return `${item.label}: ${childLabels}`;
};

// Dropdown hover handlers for collapsed state
const dropdownPosition = ref({ top: 0 });

const showTooltip = (itemLabel: string, event?: Event) => {
  if (isCollapsed.value && !isMobile.value) {
    // Cancel any pending hide timer
    if (hideTimer.value) {
      clearTimeout(hideTimer.value);
      hideTimer.value = null;
    }

    hoveredParentItem.value = itemLabel;

    // Calculate dropdown position based on hovered item
    if (event?.currentTarget) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      dropdownPosition.value = {
        top: rect.top,
      };
    }
  }
};

const hideTooltip = () => {
  // Add delay to allow mouse movement to dropdown
  hideTimer.value = window.setTimeout(() => {
    hoveredParentItem.value = null;
    hideTimer.value = null;
  }, 150); // 150ms delay
};

const cancelHideTooltip = () => {
  if (hideTimer.value) {
    clearTimeout(hideTimer.value);
    hideTimer.value = null;
  }
};

const isTooltipVisible = (itemLabel: string) => (
  isCollapsed.value && !isMobile.value && hoveredParentItem.value === itemLabel
);
</script>

<template>
  <aside
    ref="sidebarRef"
    :class="[
      'navbar navbar-vertical navbar-expand-lg navbar-dark',
      {
        'navbar-collapsed': isCollapsed && !isMobile,
        'sidebar-mobile-open': isMobile && !isCollapsed,
        'sidebar-mobile-closed': isMobile && isCollapsed,
      },
    ]"
    :style="!isMobile ? {
      width: isCollapsed ? '70px' : '280px',
      transition: 'width 0.3s ease',
    } : {}"
    data-bs-theme="dark"
  >
    <div class="container-fluid">
      <!-- Brand -->
      <div class="navbar-brand navbar-brand-autodark">
        <RouterLink to="/" class="d-flex align-items-center text-decoration-none" @click="closeSidebarOnMobile">
          <img src="/public/pwa-192x192.png" alt="SolFácil" width="32" height="32" class="navbar-brand-image me-2" />
          <span v-if="!isCollapsed || isMobile" class="fw-bold">SolFácil</span>
        </RouterLink>

        <!-- Close button for mobile -->
        <button
          v-if="isMobile"
          class="btn btn-link text-white d-lg-none p-0"
          type="button"
          @click="collapseSidebar"
        >
          <IconX :size="24" />
        </button>
      </div>

      <!-- Navigation -->
      <div class="navbar-collapse" :class="{ collapse: !isMobile }" id="sidebar-menu">
        <ul class="navbar-nav pt-lg-3">
          <template v-for="item in navItems" :key="item.label">
            <!-- Parent with children -->
            <li v-if="item.children" class="nav-item nav-parent">
              <a
                href="#"
                :class="[
                  'nav-link',
                  { active: openSubMenus.has(item.label) },
                ]"
                :title="getParentTooltip(item)"
                @click.prevent="toggleSubMenu(item.label)"
                @mouseenter="(e) => showTooltip(item.label, e)"
                @mouseleave="hideTooltip"
                @focus="(e) => showTooltip(item.label, e)"
                @blur="hideTooltip"
              >
                <span class="nav-link-icon d-md-none d-lg-inline-block">
                  <component :is="item.icon" :size="24" />
                </span>
                <span v-if="!isCollapsed || isMobile" class="nav-link-title">{{ item.label }}</span>
                <span
                  v-if="(!isCollapsed && !isMobile)"
                  :class="[
                    'dropdown-toggle-indicator',
                    { 'rotate-180': openSubMenus.has(item.label) },
                  ]"
                >
                  <IconChevronDown :size="16" />
                </span>
              </a>

              <!-- Desktop: Hover bridge for smooth navigation -->
              <div
                v-if="isTooltipVisible(item.label)"
                class="nav-dropdown-bridge"
                tabindex="-1"
                @mouseenter="cancelHideTooltip"
                @mouseleave="hideTooltip"
                @focusin="cancelHideTooltip"
                @focusout="hideTooltip"
              />

              <!-- Desktop: Dropdown menu for collapsed state -->
              <div
                v-if="isTooltipVisible(item.label)"
                class="nav-dropdown"
                :style="{ top: `${dropdownPosition.top}px` }"
                @mouseenter="cancelHideTooltip"
                @mouseleave="hideTooltip"
                @focusin="cancelHideTooltip"
                @focusout="hideTooltip"
              >
                <div class="nav-dropdown-header">
                  <component :is="item.icon" :size="24" />
                  <span>{{ item.label }}</span>
                </div>
                <ul class="nav-dropdown-menu">
                  <li v-for="child in item.children" :key="child.label">
                    <RouterLink
                      v-if="!child.external"
                      :to="getValidRoute(child.to)"
                      class="nav-dropdown-link"
                      @click="() => { hideTooltip(); closeSidebarOnMobile(); }"
                    >
                      <component :is="child.icon" :size="18" />
                      <span>{{ child.label }}</span>
                    </RouterLink>
                    <a
                      v-else
                      :href="child.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="nav-dropdown-link"
                      @click="() => { hideTooltip(); closeSidebarOnMobile(); }"
                    >
                      <component :is="child.icon" :size="18" />
                      <span>{{ child.label }}</span>
                    </a>
                  </li>
                </ul>
              </div>

              <!-- Mobile: Always show all children, Desktop: Show on dropdown -->
              <ul
                v-if="isMobile || (!isCollapsed && openSubMenus.has(item.label))"
                class="nav nav-sub nav-sub-open"
              >
                <template v-for="child in item.children" :key="child.label">
                  <li class="nav-item">
                    <RouterLink
                      v-if="!child.external"
                      :to="getValidRoute(child.to)"
                      :class="[
                        'nav-link',
                        'mobile-nav-link',
                        { 'router-link-active': isRouteActive(child.to) },
                      ]"
                      @click="closeSidebarOnMobile"
                    >
                      <span class="nav-link-icon d-md-none d-lg-inline-block">
                        <component :is="child.icon" :size="18" />
                      </span>
                      <span class="nav-link-title">{{ child.label }}</span>
                    </RouterLink>
                    <a
                      v-else
                      :href="child.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="nav-link"
                      @click="closeSidebarOnMobile"
                    >
                      <span class="nav-link-icon d-md-none d-lg-inline-block">
                        <component :is="child.icon" :size="18" />
                      </span>
                      <span class="nav-link-title">{{ child.label }}</span>
                    </a>
                  </li>
                </template>
              </ul>
            </li>

            <!-- Single item -->
            <li v-else class="nav-item nav-single">
              <RouterLink
                v-if="!item.external"
                :to="getValidRoute(item.to)"
                class="nav-link mobile-nav-link"
                :title="isCollapsed && !isMobile ? item.label : ''"
                @click="closeSidebarOnMobile"
              >
                <span class="nav-link-icon d-md-none d-lg-inline-block">
                  <component :is="item.icon" :size="24" />
                </span>
                <span v-if="!isCollapsed || isMobile" class="nav-link-title">{{ item.label }}</span>
              </RouterLink>
              <a
                v-else
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
                class="nav-link"
                :title="isCollapsed && !isMobile ? item.label : ''"
                @click="closeSidebarOnMobile"
              >
                <span class="nav-link-icon d-md-none d-lg-inline-block">
                  <component :is="item.icon" :size="24" />
                </span>
                <span v-if="!isCollapsed || isMobile" class="nav-link-title">{{ item.label }}</span>
              </a>
            </li>
          </template>
        </ul>
      </div>
    </div>

    <!-- Toggle Button Floating (desktop only) -->
    <button
      v-if="showToggleButton"
      :class="[
        'btn btn-primary btn-floating-toggle',
        { 'btn-floating-toggle-collapsed': isCollapsed },
      ]"
      type="button"
      :title="isCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'"
      @click="toggleSidebar"
    >
      <IconChevronRight v-if="isCollapsed" :size="10" />
      <IconChevronLeft v-else :size="10" />
    </button>

    <!-- Footer -->
    <div class="navbar-footer">
      <div class="d-flex align-items-center justify-content-center" style="width: 100%;">
        <!-- Logout -->
        <button
          :class="[
            'btn btn-outline-light btn-sm',
            isCollapsed ? 'btn-icon' : 'flex-fill',
          ]"
          type="button"
          :title="isCollapsed ? 'Logout' : ''"
          @click="logout.mutateAsync()"
        >
          <IconLogout :size="18" />
          <span v-if="!isCollapsed" class="ms-2">Logout</span>
        </button>
      </div>
    </div>

    <!-- Mobile Overlay -->
    <div
      v-if="isMobile && !isCollapsed"
      class="navbar-overlay"
      tabindex="0"
      @click="collapseSidebar"
      @keydown.enter="collapseSidebar"
      @keydown.space="collapseSidebar"
    />
  </aside>
</template>

<style lang="scss" scoped>
// ===== VARIÁVEIS SCSS =====
$sidebar-width-expanded: 280px;
$sidebar-width-collapsed: 70px;
$footer-height: 70px;
$btn-height: 36px;
$toggle-btn-size: 28px;

$brand-gradient: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
$green-gradient: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
$green-gradient-hover: linear-gradient(135deg, #16a34a 0%, #15803d 100%);

$white-05: rgba(255, 255, 255, 0.05);
$white-10: rgba(255, 255, 255, 0.1);
$white-20: rgba(255, 255, 255, 0.2);
$white-30: rgba(255, 255, 255, 0.3);
$white-50: rgba(255, 255, 255, 0.5);
$white-70: rgba(255, 255, 255, 0.7);
$white-80: rgba(255, 255, 255, 0.8);
$white-90: rgba(255, 255, 255, 0.9);
$black-10: rgba(0, 0, 0, 0.1);
$black-20: rgba(0, 0, 0, 0.2);
$black-50: rgba(0, 0, 0, 0.5);

$transition-default: 0.3s ease;
$transition-fast: 0.2s ease;
$border-radius: 8px;
$shadow-default: 0 4px 12px rgba(34, 197, 94, 0.3);

// ===== MIXINS =====
@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@mixin button-reset {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
}

// ===== SIDEBAR BASE =====
.navbar-vertical {
  background: $brand-gradient;
  width: $sidebar-width-expanded;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  @include flex-center;
  flex-direction: column;
  overflow: hidden;
  transition: width $transition-default;

  // Bootstrap override
  &.navbar-expand-lg > [class^="container"] {
    min-height: 0;
  }

  // Container
  .container-fluid {
    height: 100vh;
  display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
  }

  // States
  &.navbar-collapsed {
    width: $sidebar-width-collapsed;
  }

  &.sidebar-mobile-hidden {
    transform: translateX(-100%);
  }
}

// ===== BRAND SECTION =====
.navbar-brand {
  padding: 1.25rem 1rem;
  border-bottom: 1px solid $white-10;
  margin-bottom: 0;
  flex-shrink: 0;

  &-image {
    border-radius: $border-radius;
    object-fit: cover;
  }
}

// ===== NAVIGATION =====
.navbar-collapse {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.navbar-nav {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  overflow-x: hidden;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: $white-10;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: $white-30;
    border-radius: 2px;

    &:hover {
      background: $white-50;
    }
  }

  // Nav items
  .nav-item {
    width: 100%;
    display: block;
  }
}

.nav-link {
  color: $white-80;
  padding: 0.75rem 1rem;
  margin: 0.125rem 0.5rem;
  border-radius: $border-radius;
  transition: all $transition-fast;
    display: flex;
  align-items: center;
  width: calc(100% - 1rem);

  &:hover {
    color: #ffffff;
    background: $white-10;
    transform: translateX(2px);
  }

  // Remove active state from parent items
  &.router-link-active,
  &.active,
  &.router-link-exact-active {
    border-left: none;
    background: transparent;
    color: $white-80;
    box-shadow: none;
  }

  // Icon
  &-icon {
    @include flex-center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  // Title
  &-title {
    margin-left: 0.75rem;
    font-weight: 500;
  }
}

// ===== DROPDOWN =====
.dropdown-toggle-indicator {
  transition: transform $transition-fast;
  margin-left: auto;
  @include flex-center;

  &.rotate-180 {
    transform: rotate(180deg);
  }
}

// ===== SUBMENU =====
.nav-sub {
  max-height: 0;
  overflow: hidden;
  transition: max-height $transition-default;
  margin: 0;
  padding: 0;
  list-style: none;

  &-open {
    max-height: 300px;
  }

  // Mobile: Always visible
  @media (max-width: 991px) {
    max-height: none !important;
    overflow: visible !important;
    display: block !important;
  }

  .nav-item {
    width: 100%;
    display: block;
  }

  .nav-link {
    padding-left: 3rem;
    font-size: 0.875rem;
    color: $white-70;
    width: calc(100% - 1rem);

    &:hover {
      color: #ffffff;
      transform: translateX(2px);
    }

    // Active state ONLY for subitems
    &.router-link-active,
    &.router-link-exact-active {
      color: #ffffff;
      background: $green-gradient;
      box-shadow: $shadow-default;
      border-left: 3px solid #9ca3af;
      margin-left: 0.5rem;
      padding-left: calc(3rem - 3px);
    }

    &-icon {
      width: 18px;
      height: 18px;
    }
  }
}

// ===== COLLAPSED STATE =====
.navbar-collapsed {
  .nav-link {
    justify-content: center;
    padding: 0.75rem 0;

    &-title,
    .dropdown-toggle-indicator {
      display: none;
    }
  }

  // Hide subitems only on desktop collapsed (not mobile)
  .nav-sub {
    display: none;

    @media (max-width: 991px) {
      display: block !important;
    }
  }

  .navbar-footer {
    height: $footer-height;
    min-height: $footer-height;
    max-height: $footer-height;
    padding: 0.75rem;

    .d-flex {
      justify-content: center;
      align-items: center;
    }

    .btn {
      height: $btn-height;
      width: $btn-height;
      justify-content: center;
    }
  }
}

// ===== FOOTER =====
.navbar-footer {
  width: 100%;
  padding: 0.75rem;
  border-top: 1px solid $white-10;
  flex-shrink: 0;
  background: $black-10;
  height: $footer-height;
  min-height: $footer-height;
  max-height: $footer-height;
  @include flex-center;

  .btn {
    height: $btn-height;
    display: flex;
    align-items: center;
  }
}

// ===== FLOATING TOGGLE BUTTON =====
.btn-floating-toggle {
  position: fixed;
  bottom: 54px;
  left: 264px;
  width: $toggle-btn-size;
  height: $toggle-btn-size;
  border-radius: 50%;
  padding: 0;
  @include flex-center;
  background: $green-gradient;
  border: 1px solid #ffffff;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.4), 0 1px 2px $black-10;
  transition: all $transition-default;
  z-index: 9999;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(34, 197, 94, 0.6), 0 4px 8px rgba(0, 0, 0, 0.15);
    background: $green-gradient-hover;
  }

  &:active {
    transform: scale(0.95);
  }

  &-collapsed {
    left: 54px;
  }
}

// ===== MOBILE OVERLAY =====
.navbar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: $black-50;
  z-index: 1039;
}

// ===== DROPDOWN MENU FOR COLLAPSED STATE =====
// Invisible bridge for smooth mouse navigation
.nav-dropdown-bridge {
  position: fixed;
  left: $sidebar-width-collapsed - 5px;
  top: 0;
  width: 15px;
  height: 100vh;
  z-index: 9999;
  background: transparent;
  pointer-events: auto;
}

.nav-dropdown {
  position: fixed;
  left: $sidebar-width-collapsed + 8px;
  background: $brand-gradient;
  border: 1px solid $white-10;
  border-radius: $border-radius;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  min-width: 250px;
  max-width: 280px;
  opacity: 0;
  animation: fadeInDropdown 0.25s ease-out forwards;

  &-header {
    padding: 0.75rem 1rem;
    margin: 0.125rem 0.5rem;
    border-bottom: 1px solid $white-10;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 500;
    font-size: 1rem;
    color: $white-80;
    background: transparent;
    border-radius: $border-radius;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-bottom: 0;
  }

  &-menu {
    list-style: none;
    padding: 0.5rem 0;
    margin: 0;
    max-height: 400px;
    overflow-y: auto;

    li {
      margin: 0;
    }
  }

  &-link {
      display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    margin: 0.125rem 0.5rem;
    color: $white-70;
    text-decoration: none;
    transition: all $transition-fast;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: $border-radius;
    width: calc(100% - 1rem);

    &:hover {
      color: #ffffff;
      background: $white-10;
      transform: translateX(2px);
    }

    &.router-link-active,
    &.router-link-exact-active {
      color: #ffffff;
      background: $green-gradient;
      box-shadow: $shadow-default;
      border-left: 3px solid #9ca3af;
      margin-left: 0.5rem;
      padding-left: calc(1rem - 3px);
    }
  }

  // Custom scrollbar for dropdown menu
  &-menu::-webkit-scrollbar {
    width: 4px;
  }

  &-menu::-webkit-scrollbar-track {
    background: $white-10;
    border-radius: 2px;
  }

  &-menu::-webkit-scrollbar-thumb {
    background: $white-30;
    border-radius: 2px;

    &:hover {
      background: $white-50;
    }
  }
}

// Animation for dropdown appearance
@keyframes fadeInDropdown {
  from {
    opacity: 0;
    transform: translateX(-15px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

// ===== MOBILE STYLES =====
@media (max-width: 991px) {
  .navbar-vertical {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1050;
    transform: translateY(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
    pointer-events: auto !important;

    &.sidebar-mobile-open {
      transform: translateY(0);
      pointer-events: auto !important;
    }

    &.sidebar-mobile-closed {
      transform: translateY(-100%);
      pointer-events: none;
    }

    .container-fluid {
      padding: 0 !important;
      margin: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      pointer-events: auto !important;
    }
  }

  .navbar-brand {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid $white-10;
    width: 100%;
    box-sizing: border-box;
  }

  .navbar-collapse {
    width: 100% !important;
    box-sizing: border-box;
  }

  .navbar-nav {
    padding: 1rem 0;
    width: 100%;
    box-sizing: border-box;
  }

  .nav-item {
    width: 100%;

    > .nav-link {
      margin: 0.25rem 0;
      padding: 0.875rem 1.5rem;
      width: 100%;
      box-sizing: border-box;
      min-height: 52px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      pointer-events: auto !important;
      cursor: pointer !important;
      z-index: 9999 !important;
      background: transparent;
      border-radius: 0;
      border-left: 4px solid transparent;

      &:hover, &:focus, &:active {
        background: $white-10 !important;
        border-left-color: $white-30;
        text-decoration: none !important;
      }

      &.router-link-active,
      &.router-link-exact-active {
        background: $green-gradient !important;
        border-left-color: #9ca3af !important;
        color: #ffffff !important;
        box-shadow: $shadow-default;
      }

      .nav-link-icon {
        margin-right: 0.75rem;
        flex-shrink: 0;
      }

      .nav-link-title {
        flex: 1;
        text-align: left;
      }
    }
  }

  .nav-parent {
    > .nav-link {
      font-weight: 700;
      font-size: 15px;
      color: #ffffff;
      position: relative;

      &::after {
        content: '∨';
        position: absolute;
        right: 1.5rem;
        font-size: 12px;
        color: $white-70;
        transition: all 0.2s ease;
      }

      &.active::after {
        transform: rotate(180deg);
        color: #ffffff;
      }
    }
  }

  .nav-single {
    > .nav-link {
      font-weight: 400;
      font-size: 14px;
      color: $white-70;
    }
  }

  .nav-sub {
    margin: 0;
    width: 100%;
    background: transparent;

    .nav-item {
      .nav-link {
        margin: 0;
        padding: 0.75rem 1.5rem 0.75rem 3rem;
        width: 100%;
        box-sizing: border-box;
        min-height: 44px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        pointer-events: auto !important;
        cursor: pointer !important;
        z-index: 9999 !important;

        background: transparent;
        border-radius: 0;
        border-left: 4px solid transparent;
        font-weight: 400;
        font-size: 14px;
        color: $white-70;

        &:hover, &:focus, &:active {
          background: $white-05 !important;
          border-left-color: $white-20;
          color: $white-90 !important;
          text-decoration: none !important;
        }

        &.router-link-active,
        &.router-link-exact-active {
          background: $green-gradient !important;
          border-left-color: #9ca3af !important;
          color: #ffffff !important;
          box-shadow: $shadow-default;
        }

        .nav-link-icon {
          margin-right: 0.75rem;
          flex-shrink: 0;
          opacity: 0.8;
        }

        .nav-link-title {
          flex: 1;
          text-align: left;
        }
      }
    }
  }

  .btn-floating-toggle,
  .nav-dropdown,
  .nav-dropdown-bridge {
    display: none;
  }

  .navbar-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid $white-10;
    width: 100%;
    box-sizing: border-box;

    .btn {
      width: 100% !important;
      justify-content: center;
      min-height: 48px;
    }
  }
}

@media (min-width: 992px) {
  .sidebar-mobile-hidden {
    transform: none;
  }

  .sidebar-mobile-open,
  .sidebar-mobile-closed {
    position: fixed;
    width: $sidebar-width-expanded;
    height: 100vh;
    transform: none;
  }
}
</style>
